import { Box, Typography, Modal, Button, Grid, CircularProgress } from "@mui/material";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from 'react-toastify';
import { getTenantIdFromURL } from './getTenantIdFromURL';
import dashboardPreferencesService from '../api/tenantApi';
import { suggestAgingMapping } from '../api/companyApi';

const xlsx = require("xlsx");

function identifyFileByName(fileName) {
    const lowerCaseName = fileName.toLowerCase();

    const historicalKeywords = ["history", "payment", "main"];
    const isHistorical = historicalKeywords.some((keyword) =>
        lowerCaseName.includes(keyword)
    );

    const agingKeywords = ["aging"];
    const isAging = agingKeywords.some((keyword) =>
        lowerCaseName.includes(keyword)
    );

    if (isHistorical) {
        return "Payment File";
    } else if (isAging) {
        return "Invoice File";
    }

    return "Unknown";
}

function identifyFileByContent(headers) {
    const agingKeywords = ["current", "aging", "open balance", "total debt"];
    const isAgingFile = agingKeywords.some((keyword) =>
        headers.some((h) => h.includes(keyword))
    );

    const historicalKeywords = [
        "invoice",
        "payment",
        "amount",
        "document number",
        "date due",
        "payment date",
        "vat",
    ];
    const isHistoricalFile = historicalKeywords.some((keyword) =>
        headers.some((h) => h.includes(keyword))
    );

    if (isAgingFile) {
        return "Invoice File";
    } else if (isHistoricalFile) {
        return "Payment File";
    }

    return "Unknown";
}

export function identifyFile(file) {
    return new Promise(async (resolve, reject) => {
        if (!(file instanceof File)) {
            console.error("Provided input is not a valid file.");
            reject("Invalid file");
        }

        try {
            const headers = await parseCSVColumns(file);

            const processedHeaders = headers.map((header) =>
                typeof header === "string" ? header.toLowerCase() : header
            );

            let fileType = identifyFileByName(file.name);

            if (fileType === "Unknown") {
                console.log("headers", processedHeaders);
                fileType = identifyFileByContent(processedHeaders);
            }

            console.log("File Classification:", file.name, fileType);
            resolve(fileType);
        } catch (error) {
            console.error("Error processing the file:", error);
            reject(error);
        }
    });
}

export const parseCSVColumns = async (file, headerRowIndex = 0) => {
    return new Promise(async (resolve, reject) => {
        try {
            const sheetData = await readFileData(file);
            const idx = Number.isInteger(headerRowIndex) && headerRowIndex >= 0 && headerRowIndex < sheetData.length ? headerRowIndex : 0;
            const headers = (sheetData[idx] || []).map((h) => (h == null ? "" : String(h).trim())).filter((h) => h !== "");
            resolve(headers);
        } catch (error) {
            console.error("Error parsing the CSV columns:", error);
            reject(error);
        }
    });
};

export const validateColumnMapping = (mapping, requiredFields) => {
    return requiredFields.every((field) => mapping[field]);
};

export const mapToPredefinedStructure = (row, columnMapping, date) => {
    const mappedData = {};

    Object.keys(columnMapping).forEach((key) => {
        const columnName = columnMapping[key];
        const value = columnName ? row[columnName] : undefined;
        mappedData[key] = value !== undefined ? value : "";
    });
    if (date) mappedData["updatedUpTo"] = date;

    return mappedData;
};

export const mergeFiles = async (files, fileTypes, columnMappings, dateSelections, headerOffsets = {}, dropLabelsByFile = {}) => {
    const agingData = [];
    const paymentData = [];

    for (const file of files) {
        const fileType = fileTypes[file.name];
        const columnMapping = columnMappings[file.name];
        const date = dateSelections[file.name];

        const fileData = await readFileData(file);
        const resultJSON = convertToJSON(fileData, headerOffsets[file.name] || 0);

        console.log("resultJSON", resultJSON);
        if (!fileData || !Array.isArray(fileData) || fileData.length === 0) {
            console.error(
                `File ${file.name} is not properly formatted or has no data.`
            );
            continue;
        }

        const cleanedData = resultJSON.map((item) => {
            const cleanedItem = {};
            Object.keys(item).forEach((key) => {
                const trimmedKey = key.trim();
                const value = item[key] ? item[key].toString().trim() : "";
                cleanedItem[trimmedKey] = value;
            });
            return cleanedItem;
        });

        // AI mode (this file was analyzed by AIArc): skip preamble/subheader/total rows. In the
        // manual flow (no header offset / no drop labels for this file) behavior is UNCHANGED.
        const aiMode = Object.prototype.hasOwnProperty.call(dropLabelsByFile, file.name) || (headerOffsets[file.name] || 0) > 0;
        const dropLabels = new Set((dropLabelsByFile[file.name] || []).map((s) => String(s).toLowerCase().trim()));

        cleanedData.forEach((row) => {
            if (fileType === "Invoice File") {
                const mapped = mapToPredefinedStructure(row, columnMapping, date);
                if (aiMode) {
                    const name = String(mapped.name || "").trim();
                    if (!name) return;                          // drop blank/subheader rows
                    if (dropLabels.has(name.toLowerCase())) return; // drop total/subtotal rows
                }
                agingData.push(mapped);
            } else if (fileType === "Payment File") {
                paymentData.push(mapToPredefinedStructure(row, columnMapping));
            }
        });
    }

    const filteredAgingData = agingData.filter((item) =>
        Object.keys(item).some(
            (key) => key !== "updatedUpTo" && item[key] !== "" && item[key] !== 0
        )
    );

    const filteredPaymentData = paymentData.filter((item) =>
        Object.values(item).some((value) => value !== "" && value !== 0)
    );

    return { agingData: filteredAgingData, paymentData: filteredPaymentData };
};

const convertToJSON = (data, headerRowIndex = 0) => {
    const hIdx = Number.isInteger(headerRowIndex) && headerRowIndex >= 0 && headerRowIndex < data.length ? headerRowIndex : 0;
    const headers = (data[hIdx] || []).map((h) => (h == null ? "" : String(h).trim()));
    const jsonData = [];

    for (let i = hIdx + 1; i < data.length; i++) {
        const row = data[i];
        const jsonRow = {};

        for (let j = 0; j < headers.length; j++) {
            jsonRow[headers[j]] = row[j];
        }

        jsonData.push(jsonRow);
    }

    return jsonData;
};

export const readFileData = (file, sheetIndex = 0) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            try {
                const data = new Uint8Array(event.target.result);
                const workbook = xlsx.read(data, { type: "array" });
                const idx = Number.isInteger(sheetIndex) && sheetIndex >= 0 && sheetIndex < workbook.SheetNames.length ? sheetIndex : 0;
                const sheet = workbook.Sheets[workbook.SheetNames[idx]];
                const jsonData = xlsx.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });
                resolve(jsonData);
            } catch (error) {
                console.error("Error reading the file data:", error);
                reject(error);
            }
        };

        reader.readAsArrayBuffer(file);
    });
};

// Read every sheet's first rows so AIArc can pick the right one (e.g. the invoice-level ledger in
// a multi-sheet workbook). Returns [{ name, grid }] where grid is the first `sampleRows` rows.
export const readSheetsForAnalysis = (file, sampleRows = 15) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = new Uint8Array(event.target.result);
                const workbook = xlsx.read(data, { type: "array" });
                const sheets = workbook.SheetNames.map((name) => {
                    const rows = xlsx.utils.sheet_to_json(workbook.Sheets[name], { header: 1, raw: false, defval: "" });
                    return { name, grid: rows.slice(0, sampleRows).map((r) => (r || []).map((c) => (c == null ? "" : String(c)))), rowCount: rows.length };
                });
                resolve(sheets);
            } catch (error) { reject(error); }
        };
        reader.readAsArrayBuffer(file);
    });
};

// Read EVERY sheet from MULTIPLE files into one structure for format-agnostic analysis.
// Returns [{ fileName, fileIndex, sheets: [{ name, grid(sample), rowCount }] }].
export const readWorkbookForAnalysis = async (files, sampleRows = 15) => {
    const list = Array.from(files || []);
    const out = [];
    for (let i = 0; i < list.length; i++) {
        try {
            const sheets = await readSheetsForAnalysis(list[i], sampleRows);
            out.push({ fileName: list[i].name || `file ${i}`, fileIndex: i, sheets });
        } catch (e) {
            out.push({ fileName: list[i]?.name || `file ${i}`, fileIndex: i, sheets: [], error: String(e?.message || e) });
        }
    }
    return out;
};

export const AgingSummaryColumnMapper = ({
    file,
    columnMappings,
    setColumnMappings,
    headerRowIndex = 0,
}) => {
    const [columnMapping, setColumnMapping] = useState({
        name: "",
        accountID: "",
        address: "",
        current: "",
        aging15: "",
        aging30: "",
        aging45: "",
        aging60: "",
        aging90: "",
        aging120: "",
        aging180: "",
        aging365: "",
        aging60Plus: "",
        aging90Plus: "",
        aging120Plus: "",
        aging180Plus: "",
        aging365Plus: "",
        age: "",
        collectionAtRisk: "",
        transactionType: "",
        reason: "",
        marketSegment: "",
        country: "",
        city: "",
        zipCode: "",
        state: "",
        totalDebt: "",
        AvgCustomerPaymentDays: "",
    });

    const [availableColumns, setAvailableColumns] = useState([]);

    useEffect(() => {
        const readFileColumns = async () => {
            const columns = await parseCSVColumns(file, headerRowIndex);
            const trimmedColumns = columns.map((col) => col.trim());
            setAvailableColumns(trimmedColumns);
            // Respect a pre-set mapping (e.g. from "Analyze with Claude") instead of overwriting it
            // with keyword auto-mapping; only auto-map when nothing has been set yet.
            const existing = columnMappings && columnMappings[file.name];
            const hasExisting = existing && Object.values(existing).some((v) => v);
            if (hasExisting) {
                setColumnMapping((prev) => ({ ...prev, ...existing }));
            } else {
                autoFillColumnMapping(trimmedColumns);
            }
        };

        readFileColumns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file, headerRowIndex]);

    const agingSummaryKeywords = {
        name: ["customer name", "name", "company", "client", "customer"],
        accountID: ["account", "customer#", "accountid", "id"],
        address: ["address", "location", "site", "billing address"],
        current: [
            "current",
            "open balance",
            "current balance",
            "outstanding balance",
        ],
        aging15: ["15", "aging 15", "open balance 15", "15 days", "15 days aging"],
        aging30: ["30", "aging 30", "open balance 30", "30 days", "30 days aging"],
        aging45: ["45", "aging 45", "open balance 45", "45 days", "45 days aging"],
        aging60: ["60", "aging 60", "open balance 60", "60 days", "60 days aging"],
        aging90: ["90", "aging 90", "open balance 90", "90 days", "90 days aging"],
        aging120: [
            "120",
            "aging 120",
            "open balance 120",
            "120 days",
            "120 days aging",
        ],
        aging180: [
            "180",
            "aging 180",
            "open balance 180",
            "180 days",
            "180 days aging",
        ],
        aging365: [
            "365",
            "aging 365",
            "open balance 365",
            "365 days",
            "365 days aging",
        ],
        aging60Plus: ["60+", ">60", "aging 60+", "over 60", "greater than 60"],
        aging90Plus: ["90+", ">90", "aging 90+", "over 90", "greater than 90"],
        aging120Plus: [
            "120+",
            ">120",
            "aging 120+",
            "over 120",
            "greater than 120",
        ],
        aging180Plus: [
            "180+",
            ">180",
            "aging 180+",
            "over 180",
            "greater than 180",
        ],
        aging365Plus: [
            "365+",
            ">365",
            "aging 365+",
            "over 365",
            "greater than 365",
        ],
        age: ["age", "current age"],
        collectionAtRisk: ["Collection at Risk", "Risk"],
        transactionType: ["Transaction Type", "Type"],
        reason: ["reason", "notes", "explanation", "justification"],
        marketSegment: ["market segment", "segment"],
        country: ["country"],
        city: ["city"],
        zipCode: ["zip code", "postal code", "postcode", "zip"],
        state: ["state", "address state"],
        totalDebt: [
            "total",
            "amount due",
            "invoice total",
            "total amount",
            "amount",
        ],
        AvgCustomerPaymentDays: [
            "Entity: avg Customer payment Days",
            "average",
            "payment days",
        ],
    };

    const autoFillColumnMapping = (columns) => {
        const newMapping = { ...columnMapping };

        Object.entries(agingSummaryKeywords).forEach(([key, keywords]) => {
            const matchedColumn = columns.find((col) => {
                if (key === "age") {
                    return keywords.some(
                        (keyword) => col.toLowerCase().trim() === keyword.toLowerCase()
                    );
                }
                return keywords.some((keyword) =>
                    col.toLowerCase().includes(keyword.toLowerCase())
                );
            });

            if (matchedColumn) {
                newMapping[key] = matchedColumn;
            }
        });

        setColumnMapping(newMapping);
    };

    useEffect(() => {
        setColumnMappings((prevMappings) => ({
            ...prevMappings,
            [file.name]: columnMapping,
        }));
    }, [columnMapping, file.name, setColumnMappings]);

    // AR+ AI-assisted extraction: map arbitrary source columns to the canonical aging schema from
    // the headers + sample rows, then pre-fill the dropdowns. Additive — the user reviews below.
    const [aiBusy, setAiBusy] = useState(false);
    const [aiWarnings, setAiWarnings] = useState([]);
    const runAiMapping = async () => {
        setAiBusy(true);
        setAiWarnings([]);
        try {
            const rows = await readFileData(file); // 2D array incl. any preamble + header row
            const sampleRows = (rows || []).slice((headerRowIndex || 0) + 1, (headerRowIndex || 0) + 7);
            const res = await suggestAgingMapping(availableColumns, sampleRows);
            const m = res?.mappings || {};
            setColumnMapping((prev) => {
                const next = { ...prev };
                Object.entries(m).forEach(([key, val]) => {
                    if (val && val.column && Object.prototype.hasOwnProperty.call(next, key)) next[key] = val.column;
                });
                return next;
            });
            setAiWarnings(Array.isArray(res?.warnings) ? res.warnings : []);
            const n = Object.keys(m).length;
            toast.success(n ? `AI mapped ${n} field${n === 1 ? "" : "s"} — review below` : "AI couldn't confidently map columns — map manually");
        } catch (e) {
            toast.error(e?.response?.data?.message || "AI mapping failed — use manual mapping");
        } finally {
            setAiBusy(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
                <Button variant="outlined" size="small" onClick={runAiMapping} disabled={aiBusy || !availableColumns.length}
                    startIcon={aiBusy ? <CircularProgress size={14} /> : null} sx={{ textTransform: "none", fontWeight: 600 }}>
                    {aiBusy ? "Mapping…" : "✨ Auto-map with AI"}
                </Button>
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                    Matches your columns to CrediArc's aging schema from the headers + sample values. Review before importing.
                </Typography>
            </Box>
            {aiWarnings.length > 0 && (
                <Box sx={{ mb: 2, p: 1.25, borderRadius: "8px", background: "#fff8e1", border: "1px solid #ffe0a3" }}>
                    {aiWarnings.map((w, i) => (
                        <Typography key={i} sx={{ fontSize: 12, color: "#8a6d3b" }}>• {w}</Typography>
                    ))}
                </Box>
            )}
            {Object.entries(columnMapping).map(([key, value]) => (
                <FormControl fullWidth sx={{ mb: 2 }} key={key}>
                    <InputLabel>
                        {key.charAt(0).toUpperCase() +
                            key.slice(1).replace(/([A-Z])/g, " $1")}
                    </InputLabel>

                    <Select
                        value={value}
                        label={key.charAt(0).toUpperCase() + key.slice(1)}
                        onChange={(e) =>
                            setColumnMapping((prev) => ({
                                ...prev,
                                [key]: e.target.value,
                            }))
                        }>
                        {availableColumns.map((col) => (
                            <MenuItem key={col} value={col}>
                                {col}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ))}
        </Box>
    );
};

export const HistoricalPaymentColumnMapper = ({ file, setColumnMappings, headerRowIndex = 0 }) => {
    const [columnMapping, setColumnMapping] = useState({
        accountId: "",
        documentNumber: "",
        name: "",
        billingName: "",
        vatRegistration: "",
        city: "",
        state: "",
        country: "",
        zipCode: "",
        address: "",
        phone: "",
        contactName: "",
        contactEmail: "",
        type: "",
        createdFrom: "",
        memo: "",
        status: "",
        dueDate: "",
        paymentDate: "",
        invoiceDate: "",
        currency: "",
        amountForeignCurrency: "",
        invoiceAmount: "",
        amountDue: "",
        projectStatus: "",
        terms: "",
    });

    const [availableColumns, setAvailableColumns] = useState([]);

    const historicalPaymentKeywords = {
        accountId: ["account", "account id", "account number"],
        documentNumber: ["document number", "invoice", "invoice number", "document"],
        name: ["customer id", "customer name", "customer", "client", "name"],
        billingName: ["billing name", "bill to", "invoice name"],
        vatRegistration: ["vat number", "vat registration number", "vat reg. no", "vat"],
        country: ["country"],
        city: ["city"],
        state: ["state", "address state"],
        zipCode: ["zip code", "postal code", "postcode", "zip"],
        address: ["address", "location", "site", "billing address"],
        phone: ["phone", "telephone", "contact number"],
        contactEmail: ["email", "email address"],
        contactName: ["email first name", "first name"],
        type: ["type", "record type", "entry type"],
        createdFrom: ["created from", "form type", "source"],
        memo: ["memo", "notes", "remarks"],
        status: ["status", "invoice status", "payment status"],
        dueDate: ["due date", "payment due date", "date due"],
        paymentDate: ["payment date", "date paid"],
        invoiceDate: ["bill date", "invoice date", "document date", "date"],
        currency: ["currency", "invoice currency"],
        amountForeignCurrency: ["amount in foreign currency", "foreign amount", "amount (foreign)"],
        invoiceAmount: ["amount usd", "invoice amount", "total amount", "invoice total"],
        amountDue: ["amount due", "remaining balance", "balance due"],
        projectStatus: ["project status", "job status"],
        terms: ["terms", "net terms", "payment terms"]
    };

    useEffect(() => {
        const readFileColumns = async () => {
            const columns = await parseCSVColumns(file);
            const trimmedColumns = columns.map((col) => col.trim());
            setAvailableColumns(trimmedColumns);
            autoFillColumnMapping(trimmedColumns);
        };

        readFileColumns();
    }, [file]);

    const autoFillColumnMapping = (columns) => {
        const newMapping = { ...columnMapping };

        Object.entries(historicalPaymentKeywords).forEach(([key, keywords]) => {
            const matchedColumn = columns.find((col) =>
                keywords.some((keyword) =>
                    col.toLowerCase().includes(keyword.toLowerCase())
                )
            );
            if (matchedColumn) {
                newMapping[key] = matchedColumn;
            }
        });

        setColumnMapping(newMapping);
    };

    useEffect(() => {
        setColumnMappings((prevMappings) => ({
            ...prevMappings,
            [file.name]: columnMapping,
        }));
    }, [columnMapping, file.name, setColumnMappings]);

    // AR+ AI-assisted extraction: map arbitrary source columns to the canonical aging schema from
    // the headers + sample rows, then pre-fill the dropdowns. Additive — the user reviews below.
    const [aiBusy, setAiBusy] = useState(false);
    const [aiWarnings, setAiWarnings] = useState([]);
    const runAiMapping = async () => {
        setAiBusy(true);
        setAiWarnings([]);
        try {
            const rows = await readFileData(file); // 2D array incl. any preamble + header row
            const sampleRows = (rows || []).slice((headerRowIndex || 0) + 1, (headerRowIndex || 0) + 7);
            const res = await suggestAgingMapping(availableColumns, sampleRows);
            const m = res?.mappings || {};
            setColumnMapping((prev) => {
                const next = { ...prev };
                Object.entries(m).forEach(([key, val]) => {
                    if (val && val.column && Object.prototype.hasOwnProperty.call(next, key)) next[key] = val.column;
                });
                return next;
            });
            setAiWarnings(Array.isArray(res?.warnings) ? res.warnings : []);
            const n = Object.keys(m).length;
            toast.success(n ? `AI mapped ${n} field${n === 1 ? "" : "s"} — review below` : "AI couldn't confidently map columns — map manually");
        } catch (e) {
            toast.error(e?.response?.data?.message || "AI mapping failed — use manual mapping");
        } finally {
            setAiBusy(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
                <Button variant="outlined" size="small" onClick={runAiMapping} disabled={aiBusy || !availableColumns.length}
                    startIcon={aiBusy ? <CircularProgress size={14} /> : null} sx={{ textTransform: "none", fontWeight: 600 }}>
                    {aiBusy ? "Mapping…" : "✨ Auto-map with AI"}
                </Button>
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                    Matches your columns to CrediArc's aging schema from the headers + sample values. Review before importing.
                </Typography>
            </Box>
            {aiWarnings.length > 0 && (
                <Box sx={{ mb: 2, p: 1.25, borderRadius: "8px", background: "#fff8e1", border: "1px solid #ffe0a3" }}>
                    {aiWarnings.map((w, i) => (
                        <Typography key={i} sx={{ fontSize: 12, color: "#8a6d3b" }}>• {w}</Typography>
                    ))}
                </Box>
            )}
            {Object.entries(columnMapping).map(([key, value]) => (
                <FormControl fullWidth sx={{ mb: 2 }} key={key}>
                    <InputLabel>
                        {key.charAt(0).toUpperCase() +
                            key.slice(1).replace(/([A-Z])/g, " $1")}
                    </InputLabel>
                    <Select
                        value={value}
                        label={
                            key.charAt(0).toUpperCase() +
                            key.slice(1).replace(/([A-Z])/g, " $1")
                        }
                        onChange={(e) =>
                            setColumnMapping((prev) => ({
                                ...prev,
                                [key]: e.target.value,
                            }))
                        }>
                        {availableColumns.map((col) => (
                            <MenuItem key={col} value={col}>
                                {col}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ))}
            <Typography variant='caption' sx={{ mt: 2 }}>
                Select the corresponding columns from your file
            </Typography>
        </Box>
    );
};

export const SfAgingSummaryColumnMapper = ({ open, onClose, tenantId, headerRowIndex = 0 }) => {
    // console.log("🚀 ~ tenantId:", tenantId)
    const [columnMapping, setColumnMapping] = useState({
        name: "",
        accountID: "",
        address: "",
        collectionAtRisk: "",
        transactionType: "",
        reason: "",
        country: "",
        city: "",
        zipCode: "",
        state: "",
        totalDebt: "",
        billingName: "",
        vatRegistration: "",
        email: "",
        phone: "",
        invoiceNumber: "",
        invoiceDate: "",
        invoiceAmount: "",
        dueDate: "",
        payment: "",
        paymentDate: "",
        paymentPeriod: "",
        currency: "",
        amountPayment: "",
        amountUSD: "",
        amountDue: "",
    });

    useEffect(() => {
        // const fetchPreferences = async () => {
        //     try {
        //         const result = await dashboardPreferencesService.getPreferencesByTenantIdAndFeature(tenantId, "salesforce_aging_column_mapping")
        //         console.log("🚀 ~ fetchPreferences ~ result:", result)


        //         const preferences =
        //             result?.data?.preferences || null;

        //         if (preferences) {
        //             console.log("🚀 ~ preferences:", preferences)
        //             if (typeof preferences === 'string') {
        //                 preferences = JSON.parse(preferences);
        //             }
        //             setColumnMapping(preferences);
        //         } else {
        //             console.log("No existing preferences found.");
        //         }
        //     } catch (error) {
        //         console.error("Error fetching aging column preferences:", error);
        //         toast.error("Error fetching aging column preferences");
        //     }
        // };

        // if (tenantId) {
        //     fetchPreferences();
        // }
    }, [tenantId]);

    const handleSave = async (tenantId, columnMapping, onClose) => {
        try {
            // Fetch existing preferences
            const result = await dashboardPreferencesService.getPreferencesByTenantIdAndFeature(tenantId, "salesforce_aging_column_mapping")

            console.log("🚀 ~ result:", result);
            const jsonString = JSON.stringify(columnMapping);
            console.log("🚀 ~ columnMapping:", jsonString);

            if (result?.data?.getDashboardPreferences) {
                // Update existing preferences
                await dashboardPreferencesService.updateDashboardPreferencesByTenantIdAndFeature(tenantId, "salesforce_aging_column_mapping", { preferences: jsonString });
            } else {
                // Create new preferences
                await dashboardPreferencesService.createDashboardPreferences({
                    tenantId,
                    feature: "salesforce_aging_column_mapping",
                    preferences: jsonString,
                })
            }

            toast.success("Aging preferences updated successfully");
            console.log("Aging preferences updated successfully");
        } catch (error) {
            toast.error("Error updating aging preferences");
            console.error("Error updating aging preferences:", error);
        }

        console.log("Column Mapping Saved:", columnMapping);
        onClose();
    };

    const handleClose = () => {
        onClose();
    };

    const availableFields = [
        "Account Name",
        "Document Number",
        "Terms",
        "Due Date",
        "Currency of Total Amount",
        "Total Amount",
        "Currency of Amount Paid",
        "Amount Paid",
        "Amount USD",
        "Currency of Remaining Amount",
        "Remaining Amount",
        "Account Address",
        "Account Country",
        "Account City",
        "Account Zip code",
        "Account Email",
        "Account Email First Name",
    ];

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
            sx={{
                backdropFilter: "blur(2px)",
                backgroundColor: "rgb(255, 255, 255, 0.5)",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                }}
                onClick={(event) => {
                    if (event.target === event.currentTarget) {
                        handleClose();
                    }
                }}
            >
                <Box
                    sx={{
                        width: "95%",
                        maxWidth: 1200,
                        p: 4,
                        bgcolor: "background.paper",
                        borderRadius: "20px",
                        boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                    }}
                >
                    {/* Modal Heading */}
                    <Typography
                        variant="h5"
                        align="center"
                        sx={{ fontWeight: "bold", mb: 4 }}
                    >
                        Select Aging Fields
                    </Typography>

                    {/* Scrollable Container for Dropdowns */}
                    <Box
                        sx={{
                            maxHeight: "60vh",
                            overflowY: "auto",
                            pr: 2,
                            pl: 2,
                            pt: 2, // Added padding to ensure headings aren't hidden
                        }}
                    >
                        <Grid container spacing={2}>
                            {Object.entries(columnMapping).map(([key, value]) => (
                                <Grid item xs={12} sm={6} md={3} key={key}>
                                    <FormControl fullWidth>
                                        <InputLabel>
                                            {key.charAt(0).toUpperCase() +
                                                key.slice(1).replace(/([A-Z])/g, " $1")}
                                        </InputLabel>
                                        <Select
                                            value={value}
                                            onChange={(e) =>
                                                setColumnMapping((prev) => ({
                                                    ...prev,
                                                    [key]: e.target.value,
                                                }))
                                            }
                                        >
                                            {availableFields.map((field) => (
                                                <MenuItem key={field} value={field}>
                                                    {field}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    {/* Save Button */}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        sx={{
                            mt: 4,
                            alignSelf: "center",
                            width: "200px",
                        }}
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};
